/** "Sign in with your Diwata ID" — the browser side of the one-login handoff.
 *
 *  one_login_spec.md §4: we send the browser to the Gate with our client id and
 *  our REGISTERED redirect URI; the Gate returns a one-time code + our state on
 *  that callback; the code goes to the diwa backend which redeems it
 *  server-side. These are the pure pieces (URL building, state round-trip) so
 *  they test without a DOM. The gate origin always ARRIVES from the backend's
 *  /auth/gate/status (the single GATE_PUBLIC_ORIGIN config) — never hardcode a
 *  gate host here.
 */

const STATE_KEY = 'dw-gate-sso'

/** The callback URI this app registers at the gate: <origin>/auth/callback. */
export function callbackRedirectUri(origin: string): string {
  return `${origin.replace(/\/+$/, '')}/auth/callback`
}

/** Unguessable per-attempt state (CSRF binding for the round-trip). */
export function makeState(): string {
  const bytes = new Uint8Array(16)
  crypto.getRandomValues(bytes)
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
}

export function buildGateLoginUrl(
  gateOrigin: string,
  clientId: string,
  redirectUri: string,
  state: string,
): string {
  const url = new URL('/login', gateOrigin)
  url.searchParams.set('client_id', clientId)
  url.searchParams.set('redirect_uri', redirectUri)
  url.searchParams.set('state', state)
  return url.toString()
}

/** Park the state (and where the visitor was headed) for the callback leg. */
export function rememberState(storage: Storage, state: string, from: string | null = null): void {
  storage.setItem(STATE_KEY, JSON.stringify({ state, from }))
}

/** Validate + consume the parked state. Single-use: reading clears it, match or
 *  not, so a replayed callback can never pass twice. */
export function consumeState(
  storage: Storage,
  returned: string | null,
): { ok: boolean; from: string | null } {
  const raw = storage.getItem(STATE_KEY)
  storage.removeItem(STATE_KEY)
  if (!raw || !returned) return { ok: false, from: null }
  try {
    const parsed = JSON.parse(raw) as { state?: string; from?: string | null }
    return { ok: !!parsed.state && parsed.state === returned, from: parsed.from ?? null }
  } catch {
    return { ok: false, from: null }
  }
}

/** How should /login behave for this gate status?
 *
 *  The invisible standard (founder ruling 2026-08-04): when the gate flow is
 *  ON, the /login page renders NOTHING but an immediate silent redirect to the
 *  gate — no form, no button. The local form exists only for flag-off (and for
 *  older/unreachable backends, which report no gate).
 *
 *  - `undefined` (status probe still in flight) → 'pending': render a quiet
 *    holding state, never the form — a form flash before a redirect would
 *    break the silence.
 *  - enabled with a full origin+client → 'redirect'
 *  - anything else → 'form'
 */
export function loginMode(
  gate: { enabled: boolean; gate_origin: string | null; client_id: string | null } | null | undefined,
): 'pending' | 'redirect' | 'form' {
  if (gate === undefined) return 'pending'
  if (gate?.enabled && gate.gate_origin && gate.client_id) return 'redirect'
  return 'form'
}
